import type { MembershipPlan } from "./interface";
import { MEMBERSHIP_STATUS_TAGS } from "@/app/components/routes/auth_routes/CartManagement/BillingMirrorComponent/MemberShipComponent/membershipStatus";
import MembershipUsage from "@/app/components/routes/auth_routes/CartManagement/BillingMirrorComponent/MemberShipComponent/MembershipUsage";

export const BADGES = {
  BEST_MATCH: {
    label: "Best Match",
    icon: "/membership_best_match.svg",
    className: "bg-gradient-to-r from-white/0 to-white text-[#227cd0] border-y-2 border-r-2 border-[#e9c67a] pl-[28px] pr-[5px]",
  },
  RECOMMENDED: {
    label: "Recommended",
    icon: null,
    className: "bg-gradient-to-r from-[#f2e8ff] to-[#ede7f4] text-[#8a38f5] px-[5px]",
  },
  RENEW_NOW: {
    label: "Renew Now",
    icon: null,
    className: "bg-gradient-to-r from-[#faeacb] to-[#fffaf0] text-[#7a5518] border border-[#e0b869] px-[5px] tracking-wide",
  },
} as const;

const SummaryRow = ({
  label,
  value,
  valueClassName = "",
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) => (
  <div className="flex items-center gap-[4px] w-full">
    <p className="flex-1 text-[16px] font-medium">{label}</p>
    <p className={`text-[18px] font-bold ${valueClassName}`}>{value}</p>
  </div>
);

const StatusPanel = ({ plan, isHighlighted }: { plan: MembershipPlan; isHighlighted: boolean }) => {
  const tag = MEMBERSHIP_STATUS_TAGS[plan.status] ?? MEMBERSHIP_STATUS_TAGS.ACTIVE;
  const isActive = plan.status === "ACTIVE";
  const coupons = plan.coupons ?? [];
  const benefits = plan.benefits ?? [];
  const panelClassName = isActive
    ? "bg-[#f6fff0] border-[#82c068]"
    : `${isHighlighted ? "bg-[#fefaf5]" : "bg-[#fbfbfb]"} border-[#dfdede]`;

  return (
    <div
      className={`relative w-full border border-solid rounded-[20px] pt-[32px] pb-[16px] px-[32px] flex flex-col gap-[24px] ${panelClassName}`}
    >
      <span
        className={`absolute left-0 top-0 pl-[12px] pr-[10px] py-[5px] text-[14px] font-semibold rounded-tl-[20px] rounded-tr-[5px] rounded-br-[5px] rounded-bl-[5px] ${tag.className}`}
      >
        {tag.label}
      </span>

      {!isActive && (
        <div className="flex flex-col gap-[10px] w-full">
          <SummaryRow label={plan.expiryLabel} value={plan.expiry} />
          {plan.lifetimeSavings != null && (
            <SummaryRow
              label="Lifetime Savings"
              value={`₹${Math.trunc(plan.lifetimeSavings)}`}
              valueClassName="text-[#32850e]"
            />
          )}
        </div>
      )}

      {coupons.length > 0 && (
        <>
          {!isActive && <span className="w-full h-px bg-[#e3e3e3]" />}
          <div className="flex flex-col gap-[20px] w-full">
            {coupons.map((coupon, index) => (
              <MembershipUsage key={index} coupon={coupon} usages={plan.couponUsages} />
            ))}
          </div>
        </>
      )}

      {benefits.length > 0 && (
        <div className="flex flex-col gap-[10px] w-full">
          {benefits.map((benefit: any, index: number) => (
            <div key={index} className="flex items-center gap-[8px] font-medium">
              {benefit?.icon && <img src={benefit.icon} alt="" className="w-[18px] h-[18px] shrink-0" />}
              <p className="text-[16px] text-black">{benefit?.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RIBBON_CLIP = "polygon(0 0, 100% 0, calc(100% - 14px) 50%, 100% 100%, 0 100%, 14px 50%)";

const RenewCta = ({ heading }: { heading: string }) => (
  <div className="relative flex items-center justify-center w-full">
    <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-gradient-to-r from-transparent via-[#e9c67a] to-transparent" />
    <div
      className="relative flex items-center px-[32px] py-[7px] border-y border-[#e0b869] bg-gradient-to-r from-[#faeacb] via-[#fffaf0] to-[#faeacb]"
      style={{ clipPath: RIBBON_CLIP }}
    >
      <span className="absolute inset-y-0 left-0 w-[30px] bg-gradient-to-r from-[#ebd097] to-transparent" />
      <span className="absolute inset-y-0 right-0 w-[30px] bg-gradient-to-l from-[#ebd097] to-transparent" />
      <span className="absolute inset-x-0 top-0 h-px bg-white/80" />

      <p className="relative text-[14px] font-semibold uppercase tracking-[1.2px] text-[#7a5518] whitespace-nowrap pt-[2px]">
        {heading}
      </p>
    </div>
  </div>
);

const BenefitLines = ({ lines }: { lines: any[] }) => (
  <div className="flex flex-col gap-[16px] w-full">
    {lines.map((line: any, index: number) => (
      <div key={index} className="flex items-start gap-[5px]">
        <img src="/membership_tick.svg" alt="" className="w-[20px] h-[20px] shrink-0" />
        <p className="text-[18px] font-medium text-black">{typeof line === "string" ? line : line?.benefit_name}</p>
      </div>
    ))}
  </div>
);

const PlanCard = ({ plan }: { plan: MembershipPlan }) => {
  const badge = plan.badge && plan.badge !== "RECOMMENDED" /* Recommended tag hidden */ ? BADGES[plan.badge] : null;
  const isHighlighted = !!plan.highlighted;
  const isActive = plan.status === "ACTIVE";
  const benefitLines = plan.benefitLines ?? [];
  const hasPrice = plan.yearlyPrice != null;

  return (
    <div
      className={`relative w-[480px] rounded-[20px] p-[20px] flex flex-col gap-[24px] ${
        isHighlighted ? "bg-[#fff6e8] border border-[#e9c67a]" : "bg-white"
      } ${isHighlighted && !plan.status ? "golden-ring" : ""}`}
    >
      <div className={`flex flex-col w-full ${isActive ? "gap-[28px]" : "gap-[24px]"}`}>
        <div
          className={`flex items-center justify-between gap-[16px] ${hasPrice ? "pb-[16px] border-b border-[#ababab]" : ""}`}
        >
          <div className="flex items-center gap-[10px] min-w-0">
            <h3 className="text-[24px] font-bold text-black uppercase whitespace-nowrap">
              {plan?.title ? (plan.title.length > 15 ? `${plan.title.slice(0, 15)}...` : plan.title) : ""}
            </h3>
            {badge && (
              <span
                className={`relative flex items-center pb-[4px] pt-[6px] rounded-[5px] text-[14px] leading-[16px] font-semibold whitespace-nowrap ${badge.className}`}
              >
                {badge.icon && (
                  <img src={badge.icon} alt="" className="absolute left-[-9px] top-[-8px] w-[38px] h-[38px]" />
                )}
                {badge.label}
              </span>
            )}
          </div>
          {hasPrice ? (
            <div className="flex flex-col items-end gap-[4px]">
              <div className="flex items-baseline gap-[8px] whitespace-nowrap">
                {plan.originalYearlyPrice != null && (
                  <span className="text-[18px] font-medium text-[#838386] line-through decoration-[1.5px]">
                    ₹{plan.originalYearlyPrice}
                  </span>
                )}
                <p className="text-[24px] font-bold text-black">₹{plan.yearlyPrice}/year</p>
              </div>
              {plan?.discountLabel && (
                <span className="rounded-[8px] border border-[#409c18] bg-[#e6ffdc] px-[12px] py-[4px] text-[16px] font-semibold text-[#30820d] whitespace-nowrap">{plan?.discountLabel}</span>
              )}
            </div>
          ) : (
            isActive && plan.expiry && (
              <p className="text-[16px] font-medium text-black whitespace-nowrap">
                {plan.expiryLabel} <span className="font-bold text-[18px]">{plan.expiry}</span>
              </p>
            ))}
        </div>

        {plan.status ? (
          <StatusPanel plan={plan} isHighlighted={isHighlighted} />
        ) : (
          <BenefitLines lines={benefitLines} />
        )}
      </div>

      {plan.renewHeading && (
        <>
          <RenewCta heading={plan.renewHeading} />
          <BenefitLines lines={benefitLines} />
        </>
      )}
    </div>
  );
};

export default PlanCard;
